import React, {
  useCallback,
  useRef,
  useContext,
  createContext,
  useState,
  useEffect,
} from "react";
import { PlayIcon, PauseIcon } from "@heroicons/react/20/solid";
import { useWavesurfer } from "@wavesurfer/react";

// Create a context for managing global playback state
const AudioPlayerContext = createContext<{
  playingId: string | null;
  setPlayingId: (id: string | null) => void;
} | null>(null);

// Provider component to wrap around your app or the part where AudioPlayers are used
export const AudioPlayerProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [playingId, setPlayingId] = useState<string | null>(null);
  return (
    <AudioPlayerContext.Provider value={{ playingId, setPlayingId }}>
      {children}
    </AudioPlayerContext.Provider>
  );
};

// Custom hook to use the audio player context
const useAudioPlayerContext = () => {
  const context = useContext(AudioPlayerContext);
  if (!context) {
    throw new Error(
      "useAudioPlayerContext must be used within an AudioPlayerProvider"
    );
  }
  return context;
};

interface AudioPlayerProps {
  id: string; // Unique identifier for each player instance
  darkMode?: boolean;
  height?: number;
  audioBlob: Blob;
  layoutType?: "row" | "column";
}

const AudioPlayer: React.FC<AudioPlayerProps> = ({
  id,
  darkMode = true,
  height = 100,
  audioBlob,
  layoutType = "column",
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { playingId, setPlayingId } = useAudioPlayerContext();
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  useEffect(() => {
    const url = URL.createObjectURL(audioBlob);
    setAudioUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    };
  }, [audioBlob]);

  const { wavesurfer, isPlaying } = useWavesurfer({
    container: containerRef,
    height,
    waveColor: darkMode ? "#88ACB5" : "#C2E8E2",
    progressColor: darkMode ? "#B5E5CB" : "#1C5A50",
    cursorColor: darkMode ? "#58ad9f" : "#01c6a5",
    url: audioUrl || undefined,
    barGap: 3,
    barWidth: 2,
    barRadius: 3,
    barHeight: 3,
  });

  useEffect(() => {
    if (wavesurfer && audioUrl) {
      wavesurfer.load(audioUrl);
    }
  }, [wavesurfer, audioUrl]);

  useEffect(() => {
    if (wavesurfer) {
      if (playingId === id) {
        wavesurfer.play();
      } else {
        wavesurfer.pause();
      }
    }
  }, [wavesurfer, playingId, id]);

  const onPlayPause = useCallback(() => {
    if (wavesurfer) {
      if (isPlaying) {
        setPlayingId(null);
      } else {
        setPlayingId(id);
      }
    }
  }, [wavesurfer, isPlaying, setPlayingId, id]);

  const containerClassName = `flex space-x-4 p-4 rounded-lg z-20 ${
    darkMode
      ? "bg-[#1e3237] shadow-md"
      : "bg-white border border-[#B9DFD9] shadow-[0px_1px_8px_0px_rgba(0,0,0,0.10)]"
  } ${
    layoutType === "column"
      ? "flex-col items-stretch gap-5"
      : "flex-row items-center"
  }`;

  const buttonClassName = `flex-shrink-0 rounded-full p-2 focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all flex justify-center items-center ${
    darkMode
      ? "bg-[#B5E5CB] text-[#1e3237] hover:bg-[#88ACB5] focus:ring-[#88ACB5]"
      : "bg-[#1C5A50] text-[#C2E8E2] hover:bg-[#154339] focus:ring-[#1C5A50]"
  }`;

  return (
    <div className={containerClassName}>
      <button onClick={onPlayPause} className={buttonClassName}>
        {isPlaying ? (
          <PauseIcon className="h-5 w-5" />
        ) : (
          <PlayIcon className="h-5 w-5" />
        )}
      </button>
      <div className="flex-grow" ref={containerRef} />
    </div>
  );
};

export default AudioPlayer;
